/**
 * The authentication contract the rest of the app depends on.
 *
 * Two providers satisfy it: Clerk in production, and a local email prompt when the server
 * runs with `ALLOW_DEV_AUTH=1`. Screens never import Clerk directly, which is what allows the
 * app to render from the cached replica while Clerk is still unreachable.
 */

import { createContext, useContext } from 'react';

/** The signed-in account. */
export interface Session {
  userId: string;
  email: string;
}

/** Where authentication stands. */
export enum SessionStatus {
  /** The provider has not settled yet and no cached identity is available. */
  Loading = 'loading',
  /** A live session exists and replication can run. */
  Active = 'active',
  /**
   * The app is running from the cached identity because the auth provider is unreachable.
   * Local edits work and queue; replication resumes when the provider loads.
   */
  Cached = 'cached',
  /** Nobody is signed in. */
  SignedOut = 'signed_out',
}

/** Authentication state plus the operations screens may perform on it. */
export interface SessionContextValue {
  status: SessionStatus;
  session: Session | null;
  /** Returns a bearer credential for the API, or `null` when none can be obtained. */
  getToken: () => Promise<string | null>;
  /** Ends the session and clears the local identity. */
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const SessionContextProvider = SessionContext.Provider;

/**
 * Reads the session context.
 *
 * @throws Error when called outside a session provider, which is a wiring mistake.
 */
export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession must be used inside a session provider');
  return value;
}
